/* eslint-disable no-param-reassign */
import type { DrawBounds } from "./draw-bounds.ts";
import { bitmapToImageData, bitmapChannels } from "../bitmap/index.ts";
import { pixAPicKit } from "../pix-a-pic.ts";
import type { CanvasWithSourceDimensions } from "./canvas-types.ts";
import type { BuildInput } from "./build-input.ts";
import { normaliseBuildInput } from "./build-input.ts";

function* renderPictureAsPixels(
	ctx: CanvasImageSmoothing &
		CanvasDrawImage &
		CanvasImageData &
		CanvasWithSourceDimensions,
	utilityCtx: CanvasImageData & {
		readonly canvas: CanvasImageSource & {
			readonly width: number;
			readonly height: number;
		};
	},
	buildInput: BuildInput,
	explicitDrawBounds?: DrawBounds,
) {
	const { noBrickColour, ...image } = normaliseBuildInput(buildInput);
	if (
		utilityCtx.canvas.width < image.width ||
		utilityCtx.canvas.height < image.height
	) {
		throw new Error(
			`Cache canvas (${utilityCtx.canvas.width}x${utilityCtx.canvas.height}) 
			must be >= image (${image.width}x${image.height}) for pixel render`,
		);
	}

	const drawBounds = explicitDrawBounds ?? {
		x: 0,
		y: 0,
		width: ctx.canvas.width,
		height: ctx.canvas.height,
	};

	const smallData = bitmapToImageData(image);
	if (noBrickColour !== undefined) {
		const noBrickR = (noBrickColour.rgba >> 16) & 0xff;
		const noBrickG = (noBrickColour.rgba >> 8) & 0xff;
		const noBrickB = noBrickColour.rgba & 0xff;
		const {
			basePlateColour: { r: basePlateR, g: basePlateG, b: basePlateB },
		} = pixAPicKit;
		for (let i = 0; i < smallData.data.length; i += bitmapChannels) {
			const r = smallData.data[i];
			const g = smallData.data[i + 1];
			const b = smallData.data[i + 2];
			if (r === noBrickR && g === noBrickG && b === noBrickB) {
				smallData.data[i] = basePlateR;
				smallData.data[i + 1] = basePlateG;
				smallData.data[i + 2] = basePlateB;
			}
		}
	}
	utilityCtx.putImageData(smallData, 0, 0);
	yield;
	ctx.imageSmoothingEnabled = false;
	ctx.drawImage(
		utilityCtx.canvas,
		0,
		0,
		smallData.width,
		smallData.height,
		drawBounds.x,
		drawBounds.y,
		drawBounds.width,
		drawBounds.height,
	);
	ctx.imageSmoothingEnabled = true;

	// TODO: Currently not working nicely. Need to snap colours
	// during resize - "nearest neighbour". Atm it's blurring
	// const resized = yield* resizeBitmap(
	// 	image,
	// 	drawBounds.width,
	// 	drawBounds.height,
	// );
	// const resized = yield* resizeBitmapNearestNeighbour(
	// 	image,
	// 	drawBounds.width,
	// 	drawBounds.height,
	// );
	// ctx.putImageData(bitmapToImageData(resized), drawBounds.x, drawBounds.y);

	// Requires async, also not sure about node compat. Note: haven't tried it yet
	// ctx.drawImage(
	// 	createImageBitmap(bitmapToImageData(image)),
	// 	drawBounds.x,
	// 	drawBounds.y,
	// 	drawBounds.width,
	// 	drawBounds.height,
	// )

	// We used to use canvas to scale down.
	// Canvas almost definitely more efficient than resizeBitmap function,
	// but means this API relies on canvas, which doesn't work for workers.
	// ctx.imageSmoothingEnabled = false;
	// const smallCanvas = withTemporaryCanvasContext2d(
	// 	image.width,
	// 	image.height,
	// 	(tempCtx) => {
	// 		const smallData = bitmapToImageData(image);
	// 		tempCtx.putImageData(smallData, 0, 0);
	// 		return tempCtx.canvas;
	// 	},
	// );
	// ctx.drawImage(
	// 	smallCanvas,
	// 	drawBounds.x,
	// 	drawBounds.y,
	// 	drawBounds.width,
	// 	drawBounds.height,
	// );
	// ctx.imageSmoothingEnabled = true;
}

export default renderPictureAsPixels;
